/**
 * F43 (Track B) — GDPR request-tracking processor (scaffolding, D5).
 *
 * Route: /gdpr
 * Permission: gdpr_requests:read (process gated additionally on
 *   `gdpr_requests:process`)
 *
 * Export + deletion sub-queues (Tabs) + a status chip-row filter + a refined
 * 30-day SLA callout + a review→process / reject flow. Processing a delete runs
 * today's soft-delete + revokeAllSessions (data stays — no PII scrub); processing
 * an export marks it fulfilled (no archive builder at MVP). The 30-day SLA is
 * computed from `slaDueAt`.
 *
 * Data source: the F43 `gdprApi` (aliased to its fixture in tests).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Skeleton,
  Stat,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import { CalendarIcon } from '@bravemove/design-tokens/icons';
import {
  GDPR_RESOLUTION_NOTE_MAX,
  GDPR_REQUEST_STATUSES,
  type GdprRequest,
  type GdprRequestKind,
} from '@bravemove/types';
import { gdprApi, type GdprListResponse } from '../../api/gdpr';
import { useAuth } from '../../auth/useAuth';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  open: 'warning',
  in_review: 'info',
  fulfilled: 'success',
  rejected: 'danger', // red — a rejected request is a terminal "no"
  cancelled: 'neutral',
};

/** Status chip-row filter: an `'all'` pseudo-option plus every real status. */
const STATUS_FILTERS = ['all', ...GDPR_REQUEST_STATUSES] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Server page size (PU-09 — the route caps `limit` at 100). */
const PAGE_LIMIT = 50;

function daysUntil(iso: string): number {
  return Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000);
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminGdprPage() {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const toast = useToast();

  const canProcess = auth.adminUser?.permissions?.includes('gdpr_requests:process') ?? false;

  // PU-09 — the queue is server-filtered per (kind tab × status chip) and
  // offset-paginated with a load-more affordance (the old single un-filtered
  // page of 25 silently hid everything older + made the Stat cards under-count).
  const [activeKind, setActiveKind] = useState<GdprRequestKind>('delete');
  const [requests, setRequests] = useState<readonly GdprRequest[]>([]);
  const [meta, setMeta] = useState<GdprListResponse['meta'] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [processTarget, setProcessTarget] = useState<{
    request: GdprRequest;
    mode: 'process' | 'reject';
  } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchPage = useCallback(
    async (kind: GdprRequestKind, status: StatusFilter, offset: number) => {
      const res = await gdprApi.list({
        kind,
        status: status === 'all' ? '' : status,
        limit: PAGE_LIMIT,
        offset,
      });
      return res;
    },
    [],
  );

  const fetchList = useCallback(
    async (kind: GdprRequestKind = activeKind, status: StatusFilter = statusFilter) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchPage(kind, status, 0);
        setRequests(res.data);
        setMeta(res.meta);
        setHasMore(res.data.length === PAGE_LIMIT);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [activeKind, statusFilter, fetchPage, t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage(activeKind, statusFilter, requests.length);
      setRequests((prev) => [...prev, ...res.data]);
      setMeta(res.meta);
      setHasMore(res.data.length === PAGE_LIMIT);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoadingMore(false);
    }
  }, [activeKind, statusFilter, requests.length, fetchPage, t]);

  // Platform-wide Stat counts from the server meta (accurate regardless of the
  // active tab/filter/page — PU-09).
  const stats = useMemo(
    () => ({
      total: meta?.total ?? 0,
      pending: meta?.pending ?? 0,
      fulfilled: meta?.fulfilled ?? 0,
      rejected: meta?.rejected ?? 0,
    }),
    [meta],
  );

  const submit = async () => {
    if (!processTarget) return;
    setBusy(true);
    try {
      const trimmed = note.trim();
      const input = trimmed.length > 0 ? { resolutionNote: trimmed } : {};
      const next =
        processTarget.mode === 'process'
          ? await gdprApi.process(processTarget.request.id, input)
          : await gdprApi.reject(processTarget.request.id, input);
      setRequests((prev) => prev.map((r) => (r.id === next.id ? next : r)));
      toast.push({
        tone: 'success',
        title:
          processTarget.mode === 'process'
            ? t('moderation:gdpr.process.confirm')
            : t('moderation:gdpr.process.reject'),
      });
      setProcessTarget(null);
      setNote('');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  const renderStatusFilter = () => (
    <div
      role="group"
      aria-label={t('moderation:gdpr.filter.label')}
      className="flex flex-wrap items-center gap-2"
    >
      {STATUS_FILTERS.map((s) => (
        <Chip
          key={s}
          selected={statusFilter === s}
          // Server-side filter (PU-09) — the state change re-runs the fetch
          // effect (fetchList closes over the filter) from offset 0.
          onClick={() => setStatusFilter(s)}
          data-testid={`f43-gdpr-filter-${s}`}
        >
          {s === 'all' ? t('moderation:gdpr.filter.all') : t(`moderation:gdpr.status.${s}`)}
        </Chip>
      ))}
    </div>
  );

  const renderQueue = (kind: GdprRequestKind) => {
    // Rows are already server-filtered by (kind, status); only render the
    // active tab's fetch (the inactive panel re-fetches on tab switch).
    const rows = kind === activeKind ? requests : [];
    return (
      <div className="space-y-3">
        {renderStatusFilter()}
        {loading ? (
          <Skeleton className="h-32 w-full rounded-lg" />
        ) : rows.length === 0 ? (
          <EmptyState title={t('admin:f43.empty.gdprQueue')} />
        ) : (
          <ul className="space-y-2" data-testid={`f43-gdpr-list-${kind}`}>
            {rows.map((r) => {
              const left = daysUntil(r.slaDueAt);
              const overdue = left < 0;
              const pending = r.status === 'open' || r.status === 'in_review';
              return (
                <li key={r.id} data-testid={`f43-gdpr-row-${r.id}`}>
                  <Card>
                    <div className="flex flex-wrap items-start justify-between gap-3 p-3">
                      {/* Left — identity, status, the request metadata + outcome */}
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[13px] font-semibold text-ink">
                            {t(`moderation:gdpr.kind.${r.kind}`)}
                          </span>
                          <Badge tone={STATUS_TONES[r.status] ?? 'neutral'} size="sm">
                            {t(`moderation:gdpr.status.${r.status}`)}
                          </Badge>
                          <Badge tone="neutral" size="sm">
                            {t(`moderation:gdpr.created_by.${r.createdByKind}`)}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-ink-3">
                          <span className="inline-flex items-center gap-1">
                            {t('admin:gdpr.field.user', { defaultValue: 'User' })}
                            <Link
                              to={`/users/${r.userId}`}
                              className="font-mono text-accent hover:underline"
                            >
                              {r.userId.slice(0, 8)}…
                            </Link>
                          </span>
                          <span className="inline-flex items-center gap-1">
                            {t('admin:gdpr.field.requested', { defaultValue: 'Requested' })}
                            <span className="text-ink-2">
                              {formatDate(r.requestedAt ?? r.createdAt, locale)}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-1">
                            {t('admin:gdpr.field.due', { defaultValue: 'SLA due' })}
                            <span className="text-ink-2">{formatDate(r.slaDueAt, locale)}</span>
                          </span>
                          <span className="inline-flex items-center gap-1">
                            {t('admin:gdpr.field.country', { defaultValue: 'Country' })}
                            <span className="text-ink-2">{r.countryCode}</span>
                          </span>
                        </div>

                        {!pending ? (
                          <p className="text-[11.5px] text-ink-2">
                            {t('admin:gdpr.field.resolvedOn', { defaultValue: 'Resolved' })}{' '}
                            <span className="text-ink">
                              {formatDate(r.reviewedAt ?? r.fulfilledAt, locale)}
                            </span>
                            {r.resolutionNote ? (
                              <span className="text-ink-3"> — “{r.resolutionNote}”</span>
                            ) : null}
                          </p>
                        ) : null}
                      </div>

                      {/* Right — SLA countdown + the worklist actions */}
                      <div className="flex shrink-0 flex-col items-end gap-2">
                        {pending ? (
                          <Badge
                            tone={overdue ? 'danger' : 'warning'}
                            size="sm"
                            data-testid={`f43-gdpr-sla-${r.id}`}
                          >
                            {overdue
                              ? t('moderation:gdpr.sla.overdue')
                              : t('moderation:gdpr.sla.days_left', { count: left })}
                          </Badge>
                        ) : null}
                        {pending && canProcess ? (
                          <div className="flex gap-2">
                            <Button
                              variant="primary"
                              size="xs"
                              onClick={() => setProcessTarget({ request: r, mode: 'process' })}
                              data-testid={`f43-gdpr-process-${r.id}`}
                            >
                              {t('moderation:gdpr.process.confirm')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => setProcessTarget({ request: r, mode: 'reject' })}
                              data-testid={`f43-gdpr-reject-${r.id}`}
                            >
                              {t('moderation:gdpr.process.reject')}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
        {/* PU-09 — offset-paginated load-more (the route accepts limit/offset). */}
        {!loading && rows.length > 0 && hasMore ? (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void loadMore()}
              loading={loadingMore}
              data-testid="f43-gdpr-load-more"
            >
              {t('admin:gdpr.loadMore', { defaultValue: 'Load more' })}
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-gdpr-page">
      <PageHeader title={t('moderation:gdpr.title')} subtitle={t('moderation:gdpr.subtitle')} />

      {/* Summary strip + the 30-day SLA callout, side-by-side on wide screens. */}
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-stretch">
        {!loading && meta !== null && meta.total > 0 ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="f43-gdpr-stats">
            <Stat
              label={t('admin:gdpr.stat.total', { defaultValue: 'Total' })}
              value={String(stats.total)}
            />
            <Stat
              label={t('admin:gdpr.stat.pending', { defaultValue: 'Pending' })}
              value={String(stats.pending)}
            />
            <Stat
              label={t('admin:gdpr.stat.fulfilled', { defaultValue: 'Fulfilled' })}
              value={String(stats.fulfilled)}
            />
            <Stat
              label={t('admin:gdpr.stat.rejected', { defaultValue: 'Rejected' })}
              value={String(stats.rejected)}
            />
          </div>
        ) : (
          <div />
        )}
        <div
          data-testid="f43-gdpr-sla-banner"
          className="flex items-center gap-3 rounded-xl border border-info/20 bg-info/5 px-3.5 py-2.5"
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-info/15 text-info">
            <CalendarIcon size={16} color="currentColor" />
          </div>
          <div className="min-w-0">
            <p className="text-[12.5px] font-semibold text-ink">
              {t('moderation:gdpr.sla.banner_title')}
            </p>
            <p className="text-[11.5px] leading-snug text-ink-3">
              {t('moderation:gdpr.sla.banner')}
            </p>
          </div>
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      <Tabs
        value={activeKind}
        defaultValue="delete"
        // Server-side kind filter (PU-09) — the state change re-runs the fetch
        // effect (fetchList closes over the active kind) from offset 0.
        onValueChange={(v) => setActiveKind(v as GdprRequestKind)}
      >
        <TabList>
          <Tab value="delete">{t('moderation:gdpr.kind.delete')}</Tab>
          <Tab value="export">{t('moderation:gdpr.kind.export')}</Tab>
        </TabList>
        <TabPanel value="delete">{renderQueue('delete')}</TabPanel>
        <TabPanel value="export">{renderQueue('export')}</TabPanel>
      </Tabs>

      {/* Process / reject dialog */}
      <Dialog
        open={processTarget !== null}
        onClose={() => setProcessTarget(null)}
        onConfirm={() => void submit()}
        destructive={processTarget?.mode === 'process' && processTarget.request.kind === 'delete'}
        title={
          processTarget?.mode === 'reject'
            ? t('moderation:gdpr.process.reject')
            : t('moderation:gdpr.process.title')
        }
        confirmLabel={
          processTarget?.mode === 'reject'
            ? t('moderation:gdpr.process.reject')
            : t('moderation:gdpr.process.confirm')
        }
        loading={busy}
      >
        {processTarget?.mode === 'process' ? (
          <div className="mb-3" data-testid="f43-gdpr-process-warning">
            <Banner tone={processTarget.request.kind === 'delete' ? 'warning' : 'info'}>
              {processTarget.request.kind === 'delete'
                ? t('moderation:gdpr.process.delete_warning')
                : t('moderation:gdpr.process.export_warning')}
            </Banner>
          </div>
        ) : null}
        <FormField label={t('moderation:gdpr.process.resolution_note_label')}>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={GDPR_RESOLUTION_NOTE_MAX}
            placeholder={t('moderation:gdpr.process.resolution_note_placeholder')}
            rows={3}
            data-testid="f43-gdpr-note"
          />
        </FormField>
      </Dialog>
    </div>
  );
}
